import { 
  CognitoUserPool, 
  CognitoUser, 
  AuthenticationDetails, 
  CognitoUserAttribute 
} from 'amazon-cognito-identity-js';
import cognitoConfig, { configValidation } from '../utils/cognitoConfig';
import { loggerStatic as logger } from '../utils/logger';

// Create a new user pool with the configuration from cognitoConfig
let userPool;

try {
  // Log the Cognito configuration for debugging
  logger.debug('Initializing Cognito User Pool with config', {
    hasUserPoolId: !!cognitoConfig.UserPoolId,
    hasClientId: !!cognitoConfig.ClientId,
    hasRegion: !!cognitoConfig.Region
  });
  
  userPool = new CognitoUserPool({
    UserPoolId: cognitoConfig.UserPoolId,
    ClientId: cognitoConfig.ClientId,
  });
  logger.debug('Cognito User Pool initialized successfully');
} catch (error) {
  logger.error('Failed to initialize Cognito User Pool', error);
  // Create a placeholder that will throw appropriate errors when used
  userPool = {
    signUp: () => { throw new Error('Cognito User Pool not properly configured'); },
    getCurrentUser: () => null,
  };
}

// Error message mapping for user-friendly messages
const errorMessages = {
  // Cognito errors
  'UserNotConfirmedException': 'Please check your email to confirm your account.',
  'NotAuthorizedException': 'Incorrect username or password.',
  'UserNotFoundException': 'Incorrect username or password.',
  'UsernameExistsException': 'An account with this email already exists.',
  'InvalidPasswordException': 'Password does not meet requirements.',
  'LimitExceededException': 'Too many attempts. Please try again later.',
  'CodeMismatchException': 'Invalid verification code provided.',
  'ExpiredCodeException': 'Verification code has expired.',
  'TokenExpiredException': 'Your session has expired. Please sign in again.',
  
  // Generic fallbacks
  'AuthError': 'Authentication error. Please try again.',
  'NetworkError': 'Network error. Please check your connection.',
  'UnknownError': 'An unexpected error occurred. Please try again.'
};

/**
 * Check if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if the token is expired or invalid, false otherwise
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Decode the JWT (tokens are in format: header.payload.signature)
    const payload = token.split('.')[1];
    if (!payload) return true;
    
    // Base64 decode and parse the payload
    const decoded = JSON.parse(atob(payload));
    
    // Check if the token has an expiration claim
    if (!decoded.exp) return true;
    
    // Compare expiration timestamp with current time
    // exp is in seconds, Date.now() is in milliseconds
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If we can't verify, assume it's expired for safety
  }
};

// Helper function to get user-friendly error messages
export const getErrorMessage = (error) => {
  if (error.code && errorMessages[error.code]) {
    return errorMessages[error.code];
  }
  
  // Don't expose internal error details to users
  return errorMessages.UnknownError;
};

export const authService = {
  // Check if the current token is expired and handle logout if needed
  checkTokenExpiration: () => {
    const token = sessionStorage.getItem('idToken');
    
    if (isTokenExpired(token)) {
      console.log('Token expired, logging out user');
      authService.signOut();
      window.location.href = '/login?expired=true';
      return false;
    }
    
    return true;
  },

  // Sign up a new user
  signUp: (email, password, attributes = {}) => {
    logger.info('AuthService signUp called with:', { email, attributes });
    logger.debug('Using Cognito Pool Data:', cognitoConfig);
    
    return new Promise((resolve, reject) => {
      try {
        const attributeList = Object.keys(attributes).map(key => {
          logger.debug(`Creating attribute: ${key} = ${attributes[key]}`);
          return new CognitoUserAttribute({
            Name: key,
            Value: attributes[key]
          });
        });

        logger.debug('Calling Cognito signUp with:', { email, attributeList });
        userPool.signUp(email, password, attributeList, null, (err, result) => {
          if (err) {
            logger.error('Cognito signUp error:', err);
            reject(err);
            return;
          }
          
          // Check if the user needs confirmation
          if (result.userConfirmed === false) {
            logger.info('User created but requires confirmation');
          }
          
          logger.info('Cognito signUp success:', result);
          resolve(result.user);
        });
      } catch (error) {
        logger.error('Exception in signUp:', error);
        reject(error);
      }
    });
  },

  // Confirm registration with verification code
  confirmRegistration: (email, code) => {
    logger.info('Confirming registration for:', email);
    
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          logger.error('Error confirming registration:', err);
          reject(err);
          return;
        }
        logger.info('Registration confirmed successfully');
        resolve(result);
      });
    });
  },

  // Sign in a user
  signIn: (email, password) => {
    logger.debug('Attempting to sign in user');
    
    return new Promise((resolve, reject) => {
      try {
        // Make sure email is trimmed to remove any whitespace
        const trimmedEmail = email.trim();
        
        // Log the authentication data for debugging (without password)
        logger.debug('Authentication data prepared');
        
        const authenticationData = {
          Username: trimmedEmail,
          Password: password,
        };
        
        const authenticationDetails = new AuthenticationDetails(authenticationData);
        
        const userData = {
          Username: trimmedEmail,
          Pool: userPool
        };
        
        // Log the user pool configuration for debugging
        logger.debug('User pool configuration', {
          hasUserPoolId: userPool.getUserPoolId && !!userPool.getUserPoolId(),
          hasClientId: userPool.getClientId && !!userPool.getClientId()
        });
        
        const cognitoUser = new CognitoUser(userData);
        
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            logger.debug('Authentication successful');
            
            // Store authentication data in sessionStorage
            sessionStorage.setItem('accessToken', result.getAccessToken().getJwtToken());
            sessionStorage.setItem('idToken', result.getIdToken().getJwtToken());
            sessionStorage.setItem('refreshToken', result.getRefreshToken().getToken());
            sessionStorage.setItem('lastAuthenticated', Date.now().toString());
            sessionStorage.setItem('isAuthenticated', 'true');
            
            // Store the username for future use
            sessionStorage.setItem('username', trimmedEmail);
            
            resolve({
              accessToken: result.getAccessToken().getJwtToken(),
              idToken: result.getIdToken().getJwtToken(),
              refreshToken: result.getRefreshToken().getToken(),
              user: cognitoUser
            });
          },
          onFailure: (err) => {
            logger.error('Authentication error', err);
            reject(err);
          },
        });
      } catch (error) {
        logger.error('Exception in signIn', error);
        reject(error);
      }
    });
  },

  // Sign out the current user
  signOut: () => {
    console.log('Signing out user');
    
    // Clear all session storage items
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('lastAuthenticated');
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('username');
    
    // Clear any user attributes
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('user_')) {
        sessionStorage.removeItem(key);
      }
    }
    
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
      console.log('User signed out successfully from Cognito');
    } else {
      console.log('No user to sign out from Cognito');
    }
  },

  // Get current authenticated user
  getCurrentUser: async () => {
    console.log('Getting current user');
    
    return new Promise((resolve, reject) => {
      try {
        const cognitoUser = userPool.getCurrentUser();
        console.log('Cognito user from pool:', cognitoUser ? 'Found' : 'Not found');
        
        if (!cognitoUser) {
          console.log('No current user found');
          resolve(null);
          return;
        }
        
        cognitoUser.getSession((err, session) => {
          if (err) {
            console.error('Error getting session:', err);
            resolve(null);
            return;
          }
          
          console.log('Session valid:', session.isValid());
          
          if (!session.isValid()) {
            console.log('Session is invalid, returning null');
            resolve(null);
            return;
          }
          
          // Store tokens in sessionStorage for persistence across refreshes
          sessionStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
          sessionStorage.setItem('idToken', session.getIdToken().getJwtToken());
          sessionStorage.setItem('refreshToken', session.getRefreshToken().getToken());
          sessionStorage.setItem('lastAuthenticated', Date.now().toString());
          
          const tokens = {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken()
          };
          
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              console.error('Error getting user attributes:', err);
              resolve({
                user: cognitoUser,
                ...tokens
              });
              return;
            }
            
            const userData = {
              username: cognitoUser.getUsername(),
            };
            
            attributes.forEach(attr => {
              userData[attr.Name] = attr.Value;
              
              // Also store essential user data in sessionStorage
              if (['email', 'name', 'sub', 'email_verified'].includes(attr.Name)) {
                sessionStorage.setItem(`user_${attr.Name}`, attr.Value);
              }
            });
            
            console.log('Current user retrieved successfully');
            resolve({
              user: cognitoUser,
              userData,
              ...tokens
            });
          });
        });
      } catch (error) {
        console.error('Exception in getCurrentUser:', error);
        resolve(null);
      }
    });
  },

  // Forgot password
  forgotPassword: (email) => {
    logger.info('Initiating forgot password flow for:', email);
    
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          logger.info('Password reset code sent successfully');
          resolve(data);
        },
        onFailure: (err) => {
          logger.error('Error in forgot password flow:', err);
          reject(err);
        }
      });
    });
  },

  // Confirm new password after reset
  confirmPassword: (email, verificationCode, newPassword) => {
    logger.info('Confirming new password for:', email);
    
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          logger.info('Password reset successful');
          resolve();
        },
        onFailure: (err) => {
          logger.error('Error confirming new password:', err);
          reject(err);
        }
      });
    });
  }
};
